// GroupedNumberInput — Betragsfeld, das WÄHREND der Eingabe gruppiert.
//
// Bisher formatierten die Betragsfelder (EuroCellInput im Kennzahlen-Reiter,
// CurrencyInput im Erfassen-Wizard) erst beim Verlassen des Feldes: beim Tippen
// stand der Rohwert, aus „1000“ wurde erst nach dem Blur „1.000“. Der Grund war
// nie Faulheit, sondern der Cursor — jede Neuformatierung verschiebt die Zeichen,
// und ein naiv kontrolliertes Feld setzt den Cursor dabei ans Ende.
//
// Diese Komponente formatiert bei jedem Tastendruck und rechnet die Cursorposition
// mit. Die Regeln dafür (Bereinigung, Gruppierung, Caret-Arithmetik) liegen
// React-frei in `utils/numberGrouping` und sind dort ohne DOM getestet; hier wird
// nur VERDRAHTET: Feldinhalt + Cursorposition rein, Text + Zielposition raus.
//
// Der Vertrag nach außen ist derselbe wie beim `EuroCellInput`, den sie ersetzt:
// `value` ist der gespeicherte Wert (Zahl oder Rohstring), `onChange` meldet den
// GEPARSTEN Wert und feuert erst beim Blur. Damit bleibt die Integration ein
// Austausch und der Speicher-/Dirty-Fluss der Tabelle unangetastet.

import * as React from "react";
import {
  dropLeadingZero,
  formatGrouped,
  keepLeadingSign,
  reformatWithCaret,
  sanitizeGerman,
  toggleSign,
} from "../../utils/numberGrouping";
import { parseEur } from "../kennzahlen/kpiFormat";
import { aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

export type GroupedNumberInputProps = {
  /**
   * Gespeicherter Wert. Strings kommen aus dem Erfassen-Wizard (dort landen
   * Zahlfelder als roher Text im `otherInfo`-Blob), Zahlen aus den echten
   * Betragsspalten.
   */
  value: number | string | null | undefined;
  /** Beim Blur mit dem geparsten Wert gerufen; `undefined` heißt „geleert“. */
  onChange: (next: number | undefined) => void;
  /**
   * Optionaler LIVE-Kanal: meldet den angezeigten Text bei JEDEM Tastendruck.
   *
   * Nur für Aufrufer, deren Zustand während des Tippens mitlaufen MUSS. Im
   * Erfassen-Wizard hängen daran der Fortschrittsbalken und der debounced in den
   * localStorage geschriebene Entwurf: käme der Wert dort erst beim Blur an,
   * verlöre ein Kunde, der das Fenster mitten in der Eingabe schließt, das Feld.
   * Der Kanal liefert bewusst den ROHEN Anzeigetext (mit Tausenderpunkten) —
   * was davon gespeichert wird, entscheidet der Aufrufer; der gespeicherte Wert
   * bleibt `onChange` beim Blur.
   */
  onTextChange?: (text: string) => void;
  className?: string;
  placeholder?: string;
  /** Default „decimal“ — die Tastatur mit Komma/Punkt, ohne Spinner. */
  inputMode?: "decimal" | "numeric";
  /** Zugänglicher Name (Kennzahl + Jahr); Platzhalter zählen dafür nicht. */
  ariaLabel?: string;
  disabled?: boolean;
};

/**
 * Gespeicherter Wert → angezeigter (gruppierter) Text.
 *
 * Zahlen gehen über `String(v)` mit Punkt→Komma — denselben Weg ging `toRawGerman`
 * im `EuroCellInput`, die Anzeige bleibt also identisch. `toLocaleString` wäre hier
 * falsch: es rundet (Default `maximumFractionDigits: 3`) und würde beim Bearbeiten
 * gespeicherte Nachkommastellen still abschneiden.
 *
 * Exponentialschreibweise (`String(1e21)`, `String(1.2e-7)`) ergibt hier Unsinn:
 * „e" und „+/-" fallen der Bereinigung zum Opfer, aus „1,2e-7" wird „1,27".
 * Bewusst nicht behandelt — jenseits von 1e21 bzw. unter 1e-6 liegt kein
 * Euro-Betrag, und jede Sonderbehandlung müsste raten, wie viele Nachkommastellen
 * sie ausschreibt.
 */
const toDisplay = (v: number | string | null | undefined): string => {
  if (v === undefined || v === null || v === "") return "";
  const raw =
    typeof v === "number" ? (Number.isFinite(v) ? String(v).replace(".", ",") : "") : String(v);
  return formatGrouped(sanitizeGerman(raw));
};

export const GroupedNumberInput: React.FC<GroupedNumberInputProps> = ({
  value,
  onChange,
  onTextChange,
  className,
  placeholder,
  inputMode = "decimal",
  ariaLabel,
  disabled,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Der State hält ein OBJEKT statt des Strings, und das ist load-bearing:
  // Verwirft `sanitizeGerman` einen Tastendruck (Buchstabe, Währungssymbol,
  // zweites Komma), ist der neue Text mit dem alten identisch. Bei identischem
  // State-Wert rendert React NICHT neu — der useLayoutEffect unten liefe nicht und
  // die gemerkte Cursorposition würde nie gesetzt, während ReactDOM den
  // kontrollierten Wert direkt im DOM wiederherstellt und den Cursor dabei ans
  // Ende schiebt. Eine neue Objekt-Identität erzwingt den Commit, in dem die
  // Position wieder stimmt.
  const [field, setField] = React.useState<{ text: string }>(() => ({ text: toDisplay(value) }));
  /** Zielposition des Cursors für den nächsten Commit (null = nichts zu tun). */
  const caretRef = React.useRef<number | null>(null);
  const focusedRef = React.useRef(false);
  const composingRef = React.useRef(false);

  // Externe Aktualisierungen (Reload, Speichern-Antwort, Jahreswechsel) nur
  // übernehmen, solange das Feld NICHT fokussiert ist: sonst überschreibt eine
  // gerade eintreffende Antwort die laufende Eingabe mitten im Tippen.
  React.useEffect(() => {
    if (focusedRef.current) return;
    setField({ text: toDisplay(value) });
  }, [value]);

  // Cursor NACH dem Commit, aber VOR dem Paint setzen: im useEffect wäre der
  // Sprung ans Ende für einen Frame sichtbar (sichtbares Flackern bei jedem
  // Tastendruck), im useLayoutEffect nie.
  //
  // BEKANNTE, UNVERMEIDBARE EINSCHRÄNKUNG: Wert und Cursor programmatisch zu
  // setzen zerstört den nativen Undo-Stack des Feldes — Strg+Z stellt in diesem
  // Feld keine Tippschritte wieder her. Der einzige Weg, der ihn erhielte, wäre
  // `aktivesDokument().execCommand("insertText")`, und der ist deprecated. Bewusst
  // dokumentiert statt bekämpft.
  React.useLayoutEffect(() => {
    const caret = caretRef.current;
    caretRef.current = null;
    const el = inputRef.current;
    if (caret === null || !el) return;
    // Nur wenn das Feld auch den Fokus hat: ein Re-Render aus anderem Grund (die
    // Nachbarzelle wurde gespeichert) würde dem Nutzer sonst den Cursor klauen.
    if (aktivesDokument().activeElement !== el) return;
    el.setSelectionRange(caret, caret);
  });

  /**
   * Feldinhalt setzen und den LIVE-Kanal bedienen. Jede Änderung WÄHREND der
   * Eingabe läuft hier durch — der Blur bewusst nicht: dort ist `onChange` mit
   * der geparsten Zahl der Kanal nach oben (siehe `onTextChange`).
   */
  const commit = (text: string) => {
    setField({ text });
    onTextChange?.(text);
  };

  /**
   * Der generische Live-Pfad: Feldinhalt (nach der Änderung) + Cursorposition rein,
   * gruppierter Text + gemerkte Zielposition raus.
   *
   * `keepLeadingSign` und `dropLeadingZero` laufen VOR der Neuformatierung, weil
   * beide auf dem Rohstand arbeiten (verrutschtes Vorzeichen bzw. „0“ + getippte
   * Ziffer). `keepLeadingSign` braucht dafür den bisherigen Feldinhalt als
   * Vergleich — deshalb liegt es hier und nicht in `reformatWithCaret`.
   */
  const applyLive = (rawValue: string, caret: number) => {
    const signed = keepLeadingSign(field.text, rawValue, caret);
    const withoutZero = dropLeadingZero(signed.text, signed.caret);
    const next = reformatWithCaret(withoutZero.text, withoutZero.caret);
    commit(next.text);
    caretRef.current = next.caret;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    if (composingRef.current) {
      // (5) IME-Guard: während einer laufenden Komposition NICHT formatieren — das
      // Umschreiben von Wert und Cursor zerlegt sie. Der Zwischenstand wird nur
      // durchgereicht (der kontrollierte Wert muss dem DOM folgen), das
      // Nachziehen passiert einmal im onCompositionEnd.
      commit(el.value);
      return;
    }
    // (7) Markieren+Überschreiben und Einfügen brauchen keinen Sonderfall: der
    // Browser hat die Selektion bereits ersetzt, `el.value` ist der fertige neue
    // Inhalt und `selectionStart` steht hinter dem Eingefügten.
    applyLive(el.value, el.selectionStart ?? el.value.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Tastenkürzel gehören dem Browser bzw. dem System und dürfen keine der
    // Regeln unten auslösen: `e.key` ist bei Strg+„-“/Cmd+„-“ (Zoom verkleinern)
    // exakt „-“ — ohne diese Abfrage machte ein Herauszoomen in der breiten
    // Kennzahlen-Tabelle aus dem Betrag still einen negativen, ohne dass der
    // Nutzer eine Zahl angefasst hätte. Cmd/Alt/Strg+Backspace (bis Wort- bzw.
    // Zeilenanfang löschen) muss aus demselben Grund nativ durchlaufen.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // (5) IME auch hier: manche Browser liefern während einer Komposition echte
    // Tasten (keydown mit `isComposing`). Jedes `preventDefault` samt Umschreiben
    // von Wert und Cursor zerlegte sie — der `onChange`-Pfad hat denselben Guard,
    // ohne diesen wäre er nur die halbe Absicherung.
    if (composingRef.current || e.nativeEvent.isComposing) return;
    const el = e.currentTarget;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    // Ohne Cursor-Information ist keiner der Sonderfälle rechenbar → nativ
    // weiterlaufen lassen. Kann bei `type="text"` nicht passieren (siehe (6)).
    if (start === null || end === null) return;
    const collapsed = start === end;
    const text = el.value;

    // (1) Backspace direkt hinter einem Tausenderpunkt — der wichtigste Fall.
    // Nativ löscht Backspace den Punkt, die Neuformatierung setzt ihn im selben
    // Tastendruck wieder: für den Nutzer passiert NICHTS und er hämmert die Taste.
    // Der Punkt ist kein vom Nutzer getipptes Zeichen, gelöscht wird deshalb die
    // Ziffer davor (Index start-2). Der Punkt selbst bleibt stehen und fällt in
    // der Neuformatierung von allein weg, wenn die Gruppe ihn nicht mehr braucht.
    if (e.key === "Backspace" && collapsed && start >= 2 && text[start - 1] === ".") {
      e.preventDefault();
      applyLive(text.slice(0, start - 2) + text.slice(start - 1), start - 2);
      return;
    }

    // (2) Delete direkt vor einem Tausenderpunkt — spiegelbildlich zu (1), mit
    // demselben Leerlauf-Problem: gelöscht wird die Ziffer HINTER dem Punkt.
    if (e.key === "Delete" && collapsed && text[start] === "." && start + 1 < text.length) {
      e.preventDefault();
      applyLive(text.slice(0, start + 1) + text.slice(start + 2), start);
      return;
    }

    // (3) Ein getippter PUNKT bekommt bewusst KEINE Sonderregel. Er ist im
    // deutschen Format der Tausenderpunkt: `sanitizeGerman` verwirft ihn, der
    // Cursor bleibt dabei stehen (er hängt an den signifikanten Zeichen), das Feld
    // formatiert die Gruppierung ohnehin selbst. Ihn stattdessen als Dezimalkomma
    // zu deuten — Android-Dezimalpads liefern häufig nur einen Punkt — wäre genau
    // die Ratemechanik, die der Modulkopf von `numberGrouping` verbietet: aus dem
    // gewohnheitsmäßig mitgetippten „1.500.000“ würde „1,500000“ und damit still
    // 1,50 € statt 1,5 Mio. €. Das Dezimaltrennzeichen ist das Komma.

    // (4) Minus: in einer rechtsbündigen Betragszelle steht der Cursor praktisch
    // nie vorne, ein dort getipptes „-“ wäre also ein Minus in der Mitte — und das
    // verwirft `sanitizeGerman`. Es setzt deshalb das Vorzeichen an den Anfang und
    // entfernt es beim erneuten Tippen wieder.
    if (e.key === "-") {
      e.preventDefault();
      // Eine bestehende AUSWAHL muss die Regel selbst ersetzen — `preventDefault`
      // nimmt dem Browser genau diesen Schritt ab, den er sonst vor `onChange`
      // erledigt. Ohne das kippt der Alltagspfad „mit Tab in die Zelle springen“
      // (der Browser markiert dabei den ganzen Inhalt) und „-500“ tippen: die
      // Auswahl bliebe stehen, das Minus setzte nur das Vorzeichen des ALTEN
      // Werts, und aus 1.234 würde -5001234.
      const rest = collapsed ? text : `${text.slice(0, start)}${text.slice(end)}`;
      const negative = rest.startsWith("-");
      // `toggleSign` lässt alle übrigen Zeichen (inkl. Tausenderpunkte) an ihrem
      // Platz — die Positionen verschieben sich also nur um die Länge des
      // Vorzeichens. Es zählt nicht als Stelle, der Cursor bleibt hinter derselben
      // Ziffer stehen; bei einer ersetzten Auswahl steht er an deren Anfang.
      applyLive(toggleSign(rest), negative ? Math.max(0, start - 1) : start + 1);
      return;
    }
  };

  return (
    <input
      ref={inputRef}
      // (6) NIEMALS `type="number"`: Chrome liefert dort `selectionStart === null`
      // (Spec: kein Selection-API für Number-Inputs), die Caret-Arithmetik wäre
      // damit unmöglich — und der Browser verwirft einen Wert mit Tausenderpunkten
      // ohnehin als ungültig, das Feld stünde nach dem ersten Punkt leer da.
      // `inputMode` liefert die Ziffern-Tastatur ohne diese Nebenwirkungen.
      type="text"
      inputMode={inputMode}
      // Bewusst KEIN `enterKeyHint`: in der Kennzahlen-Tabelle folgen auf eine
      // Euro-Zelle Dutzende weitere, „Fertig“ auf der Software-Tastatur kündigte
      // dort das Falsche an. Ohne die Angabe entscheidet der Browser aus dem
      // Kontext (Formular vs. Einzelfeld).
      className={className}
      value={field.text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onCompositionStart={() => {
        composingRef.current = true;
      }}
      onCompositionEnd={(e) => {
        composingRef.current = false;
        const el = e.currentTarget;
        applyLive(el.value, el.selectionStart ?? el.value.length);
      }}
      onBlur={() => {
        focusedRef.current = false;
        const parsed = parseEur(field.text);
        // Beim Verlassen auf die kanonische Form normalisieren („1.000,“ → „1.000“,
        // „000“ → „0“): der Resync-Effekt oben greift nur, wenn sich `value` durch
        // das Parsen ändert — sonst bliebe ein Tipp-Zwischenstand sichtbar stehen.
        // Bewusst `setField` statt `commit`: der Blur meldet mit `onChange` die
        // ZAHL nach oben, ein zusätzlicher Text-Ruf danach wäre nur ein zweiter,
        // schwächerer Wert für dasselbe Ereignis.
        setField({ text: toDisplay(parsed) });
        onChange(parsed);
      }}
    />
  );
};
